"use client"

import * as React from "react"
import ReactMarkdown from "react-markdown"
import remarkGfm from "remark-gfm"
import rehypeRaw from "rehype-raw"
import rehypeSanitize, { defaultSchema } from "rehype-sanitize"
import { cn } from "../../lib/utils"
import { UIBox } from "../layout/ui-box"
import { createMarkdownComponents, type MarkdownVariant } from "../../lib/markdown-components"

// ============================================================================
// Extended Sanitize Schema - VSync 출력 지원
// ============================================================================

const extendedSanitizeSchema = {
  ...defaultSchema,
  attributes: {
    ...defaultSchema.attributes,
    a: [
      ...(defaultSchema.attributes?.a || []),
      ["target", "_blank"],
      ["rel", "noopener noreferrer"],
    ],
    img: [
      ...(defaultSchema.attributes?.img || []),
      "image_type",
      "data-image-id",
    ],
  },
  tagNames: [...(defaultSchema.tagNames || []), "table", "thead", "tbody", "tr", "th", "td"],
}

// ============================================================================
// Types
// ============================================================================

export interface UIMarkdownContentProps {
  /** 렌더링할 마크다운/HTML 콘텐츠 */
  content: string
  /** 콘텐츠 확장 상태 (line-clamp 적용 여부) */
  isExpanded?: boolean
  /** 접힌 상태에서 표시할 최대 줄 수 */
  maxLines?: 3 | 5 | 10
  /**
   * 변형 스타일
   * - default: 기본 문서 스타일 (큰 헤딩, 넓은 마진)
   * - compact: 압축된 스타일 (중간 헤딩, 좁은 마진)
   * - prose: 산문체 문서 스타일 (default와 동일)
   * - snippet: 검색 결과용 스타일 (작은 헤딩, 최소 마진)
   */
  variant?: MarkdownVariant
  /** GFM (GitHub Flavored Markdown) 활성화 */
  enableGfm?: boolean
  /** Raw HTML 허용 (sanitize 적용) */
  enableRawHtml?: boolean
  /** 로케일 */
  locale?: "ko" | "en"
  /** 추가 클래스 */
  className?: string
  /** 콘텐츠 영역 추가 클래스 */
  contentClassName?: string
}

// ============================================================================
// Component
// ============================================================================

/**
 * UIMarkdownContent - 마크다운/HTML 콘텐츠 렌더링 컴포넌트
 *
 * XSS 방지를 위한 sanitize와 VSync 출력의 커스텀 속성(image_type, data-image-id)을 지원합니다.
 *
 * @example
 * ```tsx
 * // 기본 사용
 * <UIMarkdownContent content="# Hello **World**" />
 *
 * // VSync 검색 결과 (확장/축소 지원)
 * <UIMarkdownContent
 *   content={result.text}
 *   isExpanded={isExpanded}
 *   maxLines={5}
 *   variant="compact"
 * />
 *
 * // 전체 문서 렌더링
 * <UIMarkdownContent
 *   content={document.content}
 *   variant="prose"
 *   enableGfm={true}
 * />
 * ```
 */
export const UIMarkdownContent = React.forwardRef<HTMLDivElement, UIMarkdownContentProps>(
  (
    {
      content,
      isExpanded = true,
      maxLines = 5,
      variant = "default",
      enableGfm = true,
      enableRawHtml = true,
      locale = "ko",
      className,
      contentClassName,
    },
    ref
  ) => {
    const markdownComponents = createMarkdownComponents(locale, variant)

    const lineClampClass = {
      3: "line-clamp-3",
      5: "line-clamp-5",
      10: "line-clamp-10",
    }[maxLines]

    // snippet variant는 prose 클래스 없이 custom 스타일만 사용
    const baseClasses = variant === 'snippet'
      ? "max-w-none text-sm"  // prose 제외, 기본 텍스트 크기만 설정
      : "prose dark:prose-invert max-w-none"

    const variantClasses = {
      default:
        "prose-sm prose-p:my-1 prose-headings:my-2 prose-ul:my-1 prose-ol:my-1 prose-li:my-0",
      compact:
        "prose-sm prose-p:my-0.5 prose-headings:my-1 prose-ul:my-0.5 prose-ol:my-0.5 prose-li:my-0",
      prose: "prose prose-gray prose-headings:mt-6 prose-headings:mb-4 prose-p:my-4",
      snippet: "",  // prose가 없으므로 prose-* 클래스 불필요
    }[variant]

    const remarkPlugins = enableGfm ? [remarkGfm] : []
    const rehypePlugins: any[] = enableRawHtml
      ? [rehypeRaw, [rehypeSanitize, extendedSanitizeSchema]]
      : []

    return (
      <UIBox
        ref={ref}
        className={cn(
          baseClasses,
          variantClasses,
          !isExpanded && lineClampClass,
          className
        )}
      >
        <ReactMarkdown
          remarkPlugins={remarkPlugins}
          rehypePlugins={rehypePlugins}
          components={markdownComponents}
          className={contentClassName}
        >
          {content}
        </ReactMarkdown>
      </UIBox>
    )
  }
)

UIMarkdownContent.displayName = "UIMarkdownContent"
